import { useCallback, useEffect, useState } from "react";
import {
  CLASS_WEEKS_MAX,
  WEEK_TITLE_MAX,
  WEEK_DOING_MAX,
  WEEK_BOOK_MAX,
  sanitizeClassPlan,
  planSay,
  type ClassPlan,
  type ClassWeek,
} from "@greg-chick/shared";
import { adminApi, adminToken } from "../lib/contentApi";
import { Msg, PageHead } from "./ui";
import { useRegisterSave } from "../lib/saveBar";

/**
 * 📅 **12주 수업 계획** — 교재와 화면을 잇는다(FR-CLASS-11).
 *
 * ## 왜 필요한가
 * 방과후 수업은 12주짜리 상품인데 화면에 「주차」가 없었다. 그림책 여섯 쪽이 전부라
 * **3주차에 무엇을 하는지 화면이 몰랐다** — 선생님이 매번 말로 안내해야 했고,
 * 결석한 아이는 따라잡을 방법이 없었다.
 *
 * ## 여기 적으면
 * · 아이 화면에 **「이번 주에 할 일」** 한 줄이 뜬다
 * · 선생님 화면에서 **[다음 주로]** 를 눌러 진도를 넘긴다
 * · 결석한 아이는 **지난 주차 것도 이어서** 할 수 있다
 *
 * ## 🔴 안 적은 주는 **안 뜬다**
 * 빈 칸을 「1주차」로 띄우면 아이 화면에 뜻 없는 줄이 하나 생기고, 선생님은 그걸 없앨
 * 방법이 없다. 그래서 이름도 할 일도 비어 있으면 그 주는 저장하지 않는다.
 *
 * ## 🔴 교재 쪽수는 **선생님만** 본다
 * 아이 화면에 「12~15쪽」이 떠 봐야 아이는 교재를 안 들고 화면을 본다.
 */
const EMPTY: ClassWeek = { n: 0, title: "", doing: "" };

export function ClassPlanPanel() {
  const [plan, setPlan] = useState<ClassPlan>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [kind, setKind] = useState<"ok" | "warn">("ok");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    void fetch(`${adminApi()}/admin/class-plan`, {
      headers: { "x-admin-token": adminToken() },
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((b: { plan?: unknown }) => {
        if (alive) setPlan(sanitizeClassPlan(b.plan));
      })
      .catch(() => {
        if (!alive) return;
        setKind("warn");
        setMsg("지금 계획을 못 불러왔어요. 저장하면 지금 화면대로 덮어써요.");
      });
    return () => {
      alive = false;
    };
  }, []);

  const save = useCallback(async () => {
    const res = await fetch(`${adminApi()}/admin/class-plan`, {
      method: "POST",
      headers: { "content-type": "application/json", "x-admin-token": adminToken() },
      body: JSON.stringify({ plan }),
    });
    if (!res.ok) throw new Error(`저장 실패 (${res.status})`);
    setDirty(false);
    setKind("ok");
    setMsg("저장했어요. 수업 중인 화면도 바로 바뀝니다.");
  }, [plan]);

  // 저장은 하단 고정 바 하나로 — 패널 안에 저장 버튼을 두지 않는다(관리자 UI 규칙)
  useRegisterSave({
    id: "classplan",
    label: "12주 수업 계획",
    dirty: dirty ? 1 : 0,
    save,
    draft: { data: () => plan, restore: (d) => setPlan(sanitizeClassPlan(d)) },
  });

  /** 그 주차의 값을 고친다. 없으면 만든다. */
  function edit(n: number, patch: Partial<ClassWeek>) {
    setPlan((p) => {
      const at = p.find((w) => w.n === n) ?? { ...EMPTY, n };
      const next = { ...at, ...patch, n };
      return [...p.filter((w) => w.n !== n), next].sort((a, b) => a.n - b.n);
    });
    setDirty(true);
  }

  const weeks = Array.from({ length: CLASS_WEEKS_MAX }, (_, i) => i + 1);
  // 🔴 저장될 모습을 그대로 보여 준다 — 화면에 친 것과 저장되는 것이 갈리면 안 된다
  const clean = sanitizeClassPlan(plan);

  return (
    <section className="panel">
      <PageHead
        icon="📅"
        title="12주 수업 계획"
        desc="주차마다 무엇을 하는지 적어요. 아이 화면에 「이번 주에 할 일」로 떠요"
      />
      <Msg text={msg} kind={kind} />

      <p className="plan__lead" data-none={clean.length === 0 ? "1" : "0"}>
        {planSay(clean)}
      </p>

      <ul className="plan__list">
        {weeks.map((n) => {
          const w = plan.find((x) => x.n === n);
          const filled = !!(w?.title || w?.doing);
          return (
            <li key={n} className="plan__row" data-filled={filled ? "1" : "0"}>
              <span className="plan__n">{n}주차</span>
              <div className="plan__fields">
                <label className="plan__f">
                  <span>이름</span>
                  <input
                    className="input"
                    value={w?.title ?? ""}
                    maxLength={WEEK_TITLE_MAX}
                    placeholder="예: 내 친구 만들기"
                    onChange={(e) => edit(n, { title: e.target.value })}
                    aria-label={`${n}주차 이름`}
                  />
                </label>
                <label className="plan__f plan__f--wide">
                  <span>아이에게 보여 줄 한 줄</span>
                  <input
                    className="input"
                    value={w?.doing ?? ""}
                    maxLength={WEEK_DOING_MAX}
                    placeholder="예: 그림책 1·2쪽을 써 봐요"
                    onChange={(e) => edit(n, { doing: e.target.value })}
                    aria-label={`${n}주차 할 일`}
                  />
                </label>
                <label className="plan__f plan__f--narrow">
                  <span>교재 쪽수</span>
                  <input
                    className="input"
                    value={w?.book ?? ""}
                    maxLength={WEEK_BOOK_MAX}
                    placeholder="12~15쪽"
                    title="선생님 화면에만 보여요. 아이 화면에는 안 떠요"
                    onChange={(e) => edit(n, { book: e.target.value })}
                    aria-label={`${n}주차 교재 쪽수`}
                  />
                </label>
              </div>
            </li>
          );
        })}
      </ul>

      <p className="plan__foot">
        비워 둔 주차는 <b>아이 화면에 안 떠요.</b> 교재 쪽수는 <b>선생님 화면에만</b> 보여요.
      </p>
    </section>
  );
}
